import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  IconButton,
  Stack,
  Collapse,
  Chip,
  Divider,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  FormProps,
  FormHeader,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

const BuscaApreensaoMenorForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showFileSection, setShowFileSection] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onFileUpload(Array.from(files));
    }
    event.target.value = '';
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  const fatosLength = (values.fatos_caso || '').trim().length;

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Ação de Busca e Apreensão de Menor com Tutela de Urgência"
        customDescription="Medida judicial urgente para localizar e recuperar menor em situação de risco ou retenção indevida"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Nome do Cliente */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Escreva o nome do seu cliente: <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: O autor é Igor Pereira Severino."
            value={values.nome_cliente || ''}
            onChange={(e) => handleFieldChange('nome_cliente', e.target.value)}
            helperText="Separe os nomes com ';' caso você represente mais de uma parte"
          />
        </Box>

        {/* 2. Fatos do Caso (Obrigatório) */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Quais são os fatos do caso? <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder="Ex: O autor é genitor(a) do menor [nome], nascido em [data], que atualmente encontra-se sob a guarda irregular do(a) requerido(a). A criança foi levada pelo(a) genitor(a) em [data] e não foi devolvida, apesar de reiterados pedidos. Há risco iminente para o menor, considerando que [descreva a situação de risco]..."
            value={values.fatos_caso || ''}
            onChange={(e) => handleFieldChange('fatos_caso', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
          <Typography
            variant="caption"
            color={fatosLength >= 200 ? 'success.main' : 'text.secondary'}
            sx={{ mt: 0.5, display: 'block' }}
          >
            {fatosLength} de 10000 caracteres permitidos
            {fatosLength < 200 && ` (mínimo: 200)`}
            {fatosLength >= 200 && ' ✓'}
          </Typography>
        </Box>

        {/* 3. Parte Contrária */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Escreva o nome da parte contrária: <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: O réu é Manuel Pereira."
            value={values.parte_contraria || ''}
            onChange={(e) => handleFieldChange('parte_contraria', e.target.value)}
            helperText="Nome da pessoa que detém ilegalmente o menor"
          />
        </Box>

        {/* 4. Upload de Documentos (Opcional) */}
        <Box>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'grey.50' },
            }}
            onClick={() => setShowFileSection(!showFileSection)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AttachFileIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Envio de documentos
                </Typography>
                <Chip label="Opcional" size="small" variant="outlined" />
              </Box>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowFileSection(!showFileSection); }}>
                {showFileSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Paper>

          <Collapse in={showFileSection}>
            <Paper variant="outlined" sx={{ p: 2, mt: -1, borderTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Sugestão de documentos para anexar:</strong> Certidão de nascimento do menor, Decisão de guarda anterior, Boletim de ocorrência, Relatórios do Conselho Tutelar e Provas de risco.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Você pode enviar um ou mais arquivos, desde que o total não ultrapasse 150MB ou 1000 páginas.
              </Typography>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,audio/*,image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />

              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  border: '2px dashed',
                  borderColor: 'grey.300',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <AttachFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" fontWeight={500}>
                  Arraste e solte os arquivos aqui ou clique para enviar
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PDF, DOCX, TXT, JPG, PNG, MP3, WAV
                </Typography>
              </Paper>

              {/* Lista de arquivos */}
              {uploadedFiles.length > 0 && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  {uploadedFiles.map((file, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}
                    >
                      <DescriptionIcon sx={{ color: 'text.secondary' }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(file.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                      <IconButton size="small" color="error" onClick={() => onFileRemove(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Paper>
          </Collapse>
        </Box>

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os fatos do caso que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="Demonstre a urgência da medida, descrevendo a situação de risco do menor e a ilegalidade da retenção. Anexe documentos que comprovem o vínculo familiar e a situação que justifica a tutela de urgência." />
    </Box>
  );
};

export default BuscaApreensaoMenorForm;

